import { FC } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useCSVReader } from 'react-papaparse';
import { Product } from '@models';
import { useGetAllProductCategoriesQuery } from '@services';
import { handleOnFileLoad } from '@helpers';
import { RsvReaderTest } from '@pages/Products/ProductCsvReader/ReaderTest';

/**
 * Simple interface for products generated from CSV data. Essentially just an ambiguous collection of key-value pairs.
 */
export type CsvProduct = { [key: string]: any };

type Props = {
  addInvalidHeader: (header: string) => void;
  addValidationError: (error: string) => void;
  clearState: () => void;
  products: Product[];
  setCustomFieldsToDelete: (customFields: { productId: number; customFieldId: number }[] | []) => void;
  setProductsToCreate: (prods: Partial<Product>[]) => void;
  setProductsToUpdate: (prods: Partial<Product>[]) => void;
  imageURL?: string;
};

const ProductCsvReader: FC<Props> = ({
  addInvalidHeader,
  addValidationError,
  clearState,
  products,
  setCustomFieldsToDelete,
  setProductsToCreate,
  setProductsToUpdate,
  imageURL,
}) => {
  const { data: categories } = useGetAllProductCategoriesQuery();
  const { CSVReader } = useCSVReader();

  return (
    <CSVReader
      data-testid={'file-uploader'}
      onUploadAccepted={(results: any) => {
        handleOnFileLoad({
          results,
          addInvalidHeader,
          addValidationError,
          clearState,
          products,
          setCustomFieldsToDelete,
          setProductsToCreate,
          setProductsToUpdate,
          imageURL,
          categories,
        });

        RsvReaderTest.testOnChooseFileClick();
      }}
    >
      {({ getRootProps, acceptedFile }: any) => (
        <>
          <Box data-testid={'csv-reader'} minWidth={400}>
            <Typography variant="subtitle1" mb={1}>
              {(acceptedFile && acceptedFile?.name) || 'No file selected'}
            </Typography>
            <Button variant="outlined" component="span" {...getRootProps()}>
              {acceptedFile ? 'Choose another file' : 'Choose file'}
            </Button>
          </Box>
        </>
      )}
    </CSVReader>
  );
};

export default ProductCsvReader;
